import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { AlertCircle, Bot, FileText, Image as ImageIcon, Mic, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, ErrorState, Skeleton, useToast } from '@/components/ui';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { ActionCard } from './action-card';
import { ConnectGoogleCard, ConnectGreenInvoiceCard, LearningRunCard, runIdOf } from './chat-cards';
import {
  assistantApi,
  type AssistantAction,
  type AssistantMessage,
  type Conversation,
  type PageContext,
} from './assistant-api';
import { Composer, type ComposerHandle, type ComposerSend } from './composer';
import { MarkdownLite } from './markdown-lite';

/* ---------------------------------------------------------------------------
   השיחה עצמה — אותו רכיב במגירה, במסך המלא ובהקמת העסק.

   שיחה נפתחת בהודעה הראשונה (לא בפתיחת המגירה), כך שלחיצה על הכפתור לא
   מייצרת שיחות ריקות. הודעות חדשות מוכרזות לקורא מסך (role="log").
   --------------------------------------------------------------------------- */

export const assistantKeys = {
  status: ['assistant', 'status'] as const,
  conversations: ['assistant', 'conversations'] as const,
  messages: (id: string) => ['assistant', 'messages', id] as const,
};

type MessagesData = Awaited<ReturnType<typeof assistantApi.messages>>;

export function useAssistantStatus() {
  return useQuery({
    queryKey: assistantKeys.status,
    queryFn: ({ signal }) => assistantApi.status(signal),
    staleTime: 5 * 60_000,
  });
}

export function ChatView({
  conversationId,
  onConversation,
  onMissing,
  mode = 'WORK',
  context,
  className,
  variant = 'panel',
}: {
  conversationId: string | null;
  onConversation?: (c: Conversation) => void;
  /** השיחה נמחקה (או שאינה של המשתמש) — מתחילים חדשה. */
  onMissing?: () => void;
  mode?: 'WORK' | 'ONBOARDING';
  context?: PageContext;
  className?: string;
  /** `hero` — בדף הבית: מצב ריק רחב, הדוגמאות בשורה. */
  variant?: 'panel' | 'hero';
}) {
  const { t } = useTranslation('assistant');
  const qc = useQueryClient();
  const toast = useToast();
  const composer = useRef<ComposerHandle>(null);
  const log = useRef<HTMLDivElement>(null);
  const [pending, setPending] = useState<ComposerSend | null>(null);

  const status = useAssistantStatus();
  const unavailable = status.data?.available === false;

  const messages = useQuery({
    queryKey: assistantKeys.messages(conversationId ?? 'none'),
    queryFn: ({ signal }) => assistantApi.messages(conversationId!, signal),
    enabled: Boolean(conversationId),
  });

  const send = useMutation({
    mutationFn: async (input: ComposerSend) => {
      let id = conversationId;
      if (!id) {
        const created = await assistantApi.create(mode);
        id = created.id;
        qc.setQueryData<MessagesData>(assistantKeys.messages(id), {
          conversation: created,
          messages: [],
        });
        onConversation?.(created);
      }
      return assistantApi.send(id, {
        text: input.text,
        files: input.files,
        audio: input.audio,
        context,
      });
    },
    onMutate: (input) => setPending(input),
    onSettled: () => setPending(null),
    onSuccess: (res) => {
      qc.setQueryData<MessagesData>(assistantKeys.messages(res.conversation.id), (prev) => ({
        conversation: res.conversation,
        messages: [...(prev?.messages ?? []), res.userMessage, res.assistantMessage],
      }));
      void qc.invalidateQueries({ queryKey: assistantKeys.conversations });
    },
    onError: (err: Error) => toast.error(t('message.failed'), err.message),
  });

  const missing = messages.error instanceof ApiError && messages.error.status === 404;
  useEffect(() => {
    if (missing) onMissing?.();
  }, [missing, onMissing]);

  const list = messages.data?.messages ?? [];
  useEffect(() => {
    // גוללים את השיחה עצמה ולא את הדף: בדף הבית השיחה היא חלק מעמוד ארוך,
    // ו-scrollIntoView היה מקפיץ את כל המסך לתחתית השיחה.
    const el = log.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [list.length, pending]);

  const updateAction = (next: AssistantAction) => {
    if (!conversationId) return;
    qc.setQueryData<MessagesData>(assistantKeys.messages(conversationId), (prev) =>
      prev
        ? {
            ...prev,
            messages: prev.messages.map((m) => ({
              ...m,
              actions: m.actions.map((a) => (a.id === next.id ? next : a)),
            })),
          }
        : prev,
    );
    // סיום ההקמה: מסך ההקמה מחליט לפי השלב בשרת.
    if (next.kind === 'finish_setup' && next.status === 'CONFIRMED') {
      void qc.invalidateQueries({ queryKey: ['onboarding-import', 'status'] });
    }
  };

  const onSend = async (input: ComposerSend) => {
    try {
      await send.mutateAsync(input);
      return true;
    } catch {
      return false;
    }
  };

  const examples = [
    t('empty.examples.quote'),
    t('empty.examples.schedule'),
    t('empty.examples.summary'),
  ];

  return (
    <div className={cn('flex min-h-0 flex-1 flex-col', className)}>
      <div
        ref={log}
        role="log"
        aria-live="polite"
        aria-label={t('title')}
        className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3"
      >
        {unavailable ? (
          <div
            role="alert"
            className="flex gap-2 rounded-(--radius-md) border border-warning-border bg-warning-subtle p-3 text-sm"
          >
            <AlertCircle className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
            <div>
              <p className="font-medium">{t('unavailable.title')}</p>
              <p className="text-xs text-fg-muted">{t('unavailable.description')}</p>
            </div>
          </div>
        ) : null}

        {conversationId && messages.isPending ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-2/3" />
            <Skeleton className="ms-auto h-10 w-1/2" />
          </div>
        ) : null}

        {!conversationId && !pending && !unavailable && mode === 'WORK' && variant === 'hero' ? (
          // בדף הבית הכותרת כבר מעל השיחה — כאן רק הדוגמאות, בשורה.
          <div className="flex h-full flex-col items-center justify-end gap-2 pb-1">
            <p className="text-xs text-fg-muted">{t('empty.description')}</p>
            <ul className="flex flex-wrap justify-center gap-2">
              {examples.map((ex) => (
                <li key={ex}>
                  <button
                    type="button"
                    className="rounded-full border border-border bg-surface px-3 py-1.5 text-start text-xs text-fg transition-colors hover:border-accent-border hover:bg-accent-subtle/50"
                    onClick={() => composer.current?.setText(ex)}
                  >
                    {ex}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {!conversationId && !pending && !unavailable && mode === 'WORK' && variant === 'panel' ? (
          <div className="px-2 py-6 text-center">
            <Sparkles className="mx-auto mb-2 size-6 text-accent" aria-hidden />
            <p className="font-medium text-fg">{t('empty.title')}</p>
            <p className="mt-1 text-xs text-fg-muted">{t('empty.description')}</p>
            <ul className="mt-3 space-y-1.5">
              {examples.map((ex) => (
                <li key={ex}>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="h-auto w-full whitespace-normal py-2 text-start"
                    onClick={() => composer.current?.setText(ex)}
                  >
                    {ex}
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {messages.isError && !missing ? (
          <ErrorState error={messages.error} onRetry={() => void messages.refetch()} />
        ) : null}

        {list.map((m) => (
          <MessageBubble key={m.id} message={m} onActionChange={updateAction} />
        ))}

        {pending ? (
          <>
            <PendingBubble input={pending} />
            <p className="flex items-center gap-2 text-xs text-fg-muted" role="status">
              <span className="flex gap-0.5" aria-hidden>
                <span className="size-1.5 animate-bounce rounded-full bg-fg-subtle" />
                <span className="size-1.5 animate-bounce rounded-full bg-fg-subtle [animation-delay:150ms]" />
                <span className="size-1.5 animate-bounce rounded-full bg-fg-subtle [animation-delay:300ms]" />
              </span>
              {t('message.typing')}
            </p>
          </>
        ) : null}
      </div>

      <Composer
        ref={composer}
        onSend={onSend}
        disabled={unavailable}
        busy={send.isPending}
        voice={status.data?.voice !== false}
      />
    </div>
  );
}

function MessageBubble({
  message,
  onActionChange,
}: {
  message: AssistantMessage;
  onActionChange: (a: AssistantAction) => void;
}) {
  const { t } = useTranslation('assistant');
  const router = useRouter();
  const mine = message.role === 'user';
  const files = message.attachments.filter((a) => a.kind !== 'audio');
  return (
    <div className={cn('flex gap-2', mine ? 'justify-end' : 'justify-start')}>
      {!mine ? (
        <span
          className="mt-1 grid size-7 shrink-0 place-items-center rounded-full bg-accent-subtle text-accent"
          aria-hidden
        >
          <Bot className="size-4" />
        </span>
      ) : null}
      <div className={cn('min-w-0', mine ? 'max-w-[85%]' : 'max-w-[92%] flex-1')}>
        <span className="sr-only">{mine ? t('message.you') : t('message.assistant')}: </span>
        {message.content ? (
          <div
            className={cn(
              'rounded-(--radius-lg) px-3 py-2 text-sm',
              mine
                ? 'bg-accent text-fg-on-accent'
                : 'border border-border bg-surface-raised text-fg',
            )}
          >
            {message.transcribed && mine ? (
              <span className="mb-0.5 flex items-center gap-1 text-2xs opacity-80">
                <Mic className="size-3" aria-hidden />
                {t('voice.transcribed')}
              </span>
            ) : null}
            {mine ? (
              <p className="whitespace-pre-wrap">{message.content}</p>
            ) : (
              <MarkdownLite text={message.content} />
            )}
          </div>
        ) : null}
        {files.length ? (
          <AttachmentChips
            names={files.map((f) => ({ name: f.name, image: f.kind === 'image' }))}
          />
        ) : null}
        {message.links.map((l) => {
          if (l.kind === 'connect_google') return <ConnectGoogleCard key={l.kind} />;
          if (l.kind === 'connect_green_invoice') return <ConnectGreenInvoiceCard key={l.kind} />;
          const runId = l.kind === 'learn' ? runIdOf(l.href) : null;
          if (runId) return <LearningRunCard key={l.href} runId={runId} href={l.href} />;
          return l.href.startsWith('/') ? (
            <Button
              key={l.href}
              variant="link"
              size="sm"
              className="mt-1"
              onClick={() => router.history.push(l.href)}
            >
              {l.kind === 'learn' ? t('message.learnLink') : l.href}
            </Button>
          ) : null;
        })}
        {message.actions.map((a) => (
          <ActionCard key={a.id} action={a} onChange={onActionChange} />
        ))}
      </div>
    </div>
  );
}

function PendingBubble({ input }: { input: ComposerSend }) {
  const { t } = useTranslation('assistant');
  return (
    <div className="flex justify-end">
      <div className="max-w-[85%] opacity-70">
        {input.text ? (
          <div className="rounded-(--radius-lg) bg-accent px-3 py-2 text-sm whitespace-pre-wrap text-fg-on-accent">
            {input.text}
          </div>
        ) : input.audio ? (
          <div className="flex items-center gap-1 rounded-(--radius-lg) bg-accent px-3 py-2 text-sm text-fg-on-accent">
            <Mic className="size-3.5" aria-hidden />
            {t('voice.transcribed')}
          </div>
        ) : null}
        {input.files.length ? (
          <AttachmentChips
            names={input.files.map((f) => ({ name: f.name, image: f.type.startsWith('image/') }))}
          />
        ) : null}
      </div>
    </div>
  );
}

function AttachmentChips({ names }: { names: Array<{ name: string; image: boolean }> }) {
  const { t } = useTranslation('assistant');
  return (
    <ul className="mt-1 flex flex-wrap justify-end gap-1" aria-label={t('message.attachments')}>
      {names.map((f, i) => (
        <li
          key={`${f.name}-${i}`}
          className="flex max-w-56 items-center gap-1 rounded-full border border-border bg-surface-sunken px-2 py-0.5 text-2xs"
        >
          {f.image ? (
            <ImageIcon className="size-3 shrink-0" aria-hidden />
          ) : (
            <FileText className="size-3 shrink-0" aria-hidden />
          )}
          <span className="truncate">{f.name}</span>
        </li>
      ))}
    </ul>
  );
}
